import React from 'react'
import { cn } from './cn'

interface StateBoxProps {
  icon: React.ReactNode
  iconTone: 'neutral' | 'error'
  title: React.ReactNode
  description: React.ReactNode
  joined?: boolean
  role?: React.AriaRole
  /** 標題層級。預設 3（頁面內的一塊區域）；整頁只有這個狀態時傳 1，讀屏才有 h1 可導覽。 */
  headingLevel?: 1 | 2 | 3
  children?: React.ReactNode
}

function StateBox({ icon, iconTone, title, description, joined, role, headingLevel = 3, children }: StateBoxProps) {
  const Heading = `h${headingLevel}` as const
  return (
    <div role={role} className={cn('rounded-[var(--radius)] border border-border bg-background px-6 py-14 text-center', joined && 'rounded-t-none border-t-0')}>
      <div
        className={cn(
          'mx-auto mb-4 grid size-11 place-items-center rounded-[11px] [&_svg]:size-5',
          iconTone === 'error' ? 'bg-[var(--error-soft)] text-error' : 'bg-surface text-muted'
        )}
        aria-hidden="true"
      >
        {icon}
      </div>
      <Heading className="mb-1.5">{title}</Heading>
      <p className="mx-auto mb-4.5 max-w-[42ch] text-sm text-muted">{description}</p>
      {children}
    </div>
  )
}

export interface EmptyStateProps {
  icon: React.ReactNode
  title: React.ReactNode
  /**
   * 說明文字。`ReactNode` 而非 `string`：邀請落地頁的失效／過期狀態要在同一段說明裡
   * 分兩句、並把收件人 email 標成墨色，那是排版而不是另一顆元件。
   */
  description: React.ReactNode
  action?: React.ReactNode
  joined?: boolean
  headingLevel?: 1 | 2 | 3
}

/**
 * App 面的空狀態（design-guideline §3.5）：不畫卡片、不畫圖示磚，上方一張 160×88 線稿，
 * 標題、一句說明、一個動作置中。卡片與圖示磚的有無是結構差異，所以是 variant。
 * 不是「還沒有東西」而是「現在做不了」（停用、沒有權限）時，上方換成 `mark`
 * （`StatusMark`），不放線稿。
 */
interface IllustratedEmptyStateBase {
  variant: 'illustrated'
  title: React.ReactNode
  description: React.ReactNode
  /** 一顆或並排的幾顆按鈕。 */
  action?: React.ReactNode
  headingLevel?: 1 | 2 | 3
}

export type IllustratedEmptyStateProps = IllustratedEmptyStateBase &
  (
    | { /** 160×88 線稿（裝飾，aria-hidden）。 */ art: React.ReactNode; mark?: never }
    | { /** 圓框狀態圖示（`StatusMark`）。 */ mark: React.ReactNode; art?: never }
  )

export function EmptyState(props: (EmptyStateProps & { variant?: 'card' }) | IllustratedEmptyStateProps) {
  if (props.variant === 'illustrated') return <IllustratedEmptyState {...props} />
  const { icon, title, description, action, joined, headingLevel } = props
  return <StateBox icon={icon} iconTone="neutral" title={title} description={description} joined={joined} headingLevel={headingLevel}>{action}</StateBox>
}

function IllustratedEmptyState({ art, mark, title, description, action, headingLevel = 3 }: IllustratedEmptyStateProps) {
  const Heading = `h${headingLevel}` as const
  return (
    <div
      data-empty-state="illustrated"
      className="flex flex-1 flex-col items-center justify-center px-[var(--m-gutter)] pb-[var(--gap-lg)] text-center"
    >
      {mark != null ? (
        <div className="mb-[var(--gap-md)] flex justify-center">{mark}</div>
      ) : (
        <div className="mb-[var(--gap-md)] text-foreground [&_svg]:h-22 [&_svg]:w-40" aria-hidden="true">
          {art}
        </div>
      )}
      {/* Sans, whatever the level: the global h1/h2 serif is for fixed page titles. */}
      <Heading className="font-sans text-lg font-semibold leading-[1.4]">{title}</Heading>
      <p className="mt-1.5 text-md leading-[var(--leading-body)] text-muted">{description}</p>
      {action != null ? (
        <div className="mt-[var(--gap-md)] flex justify-center gap-[var(--gap-sm)]">{action}</div>
      ) : null}
    </div>
  )
}

export interface ErrorStateProps extends EmptyStateProps {
  /** Mono diagnostic line, e.g.「錯誤代碼 · PROMO_LIST_502」。 */
  code?: string
}

export function ErrorState({ icon, title, description, action, code, joined, headingLevel }: ErrorStateProps) {
  return (
    <StateBox icon={icon} iconTone="error" title={title} description={description} joined={joined} role="alert" headingLevel={headingLevel}>
      {action}
      {code ? <div className="mt-3 font-mono text-xs text-muted">{code}</div> : null}
    </StateBox>
  )
}
